import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { projectService } from '@/services/project'
import type { Project } from '@/types/api'

export const useProjectStore = defineStore('project', () => {
  // State
  const projects = ref<Project[]>([])
  const currentProject = ref<Project | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  // Backend-driven filtering - no hardcoded enum values
  const getProjectsByType = computed(() => (typeValue: string) => 
    projects.value.filter(project => project.type.value === typeValue)
  )

  const getProjectsByStatus = computed(() => (statusValue: string) => 
    projects.value.filter(project => project.status.value === statusValue)
  )

  const getProjectsByDevelopmentLevel = computed(() => (levelValue: string) => 
    projects.value.filter(project => project.development_level.value === levelValue)
  )

  const currentProjectChildren = computed(() => {
    if (!currentProject.value) return []
    
    return projects.value.filter(project => 
(project.hierarchy?.parent_id || project.parent_id) === currentProject.value!.id
    )
  })

  const projectsByParent = computed(() => {
    const grouped: Record<string, Project[]> = {}
    
    projects.value.forEach(project => {
      const parentId = project.hierarchy?.parent_id || project.parent_id || 'root'
      if (!grouped[parentId]) {
        grouped[parentId] = []
      }
      grouped[parentId].push(project)
    })
    
    return grouped
  })

  const currentProjectPath = computed(() => {
    if (!currentProject.value) return []
    
    const pathIds = (currentProject.value.hierarchy?.hierarchy_path || currentProject.value.hierarchy_path || '').split('/')
    return pathIds.map(id => 
      projects.value.find(p => p.id === id)
    ).filter(Boolean) as Project[]
  })

  // Actions
  const loadUserProjects = async (forced: boolean = false): Promise<boolean> => {
    // Prevent duplicate calls if already loading or projects already loaded (unless forced)
    if (!forced && (isLoading.value || projects.value.length > 0)) {
      console.log('Projects already loading or loaded, skipping duplicate call...')
      return true
    }

    isLoading.value = true
    error.value = null
    console.log('Loading user projects...')

    try {
      const response = await projectService.getUserProjects()
      
      if (response.success && response.projects) {
        projects.value = response.projects
        console.log(`Loaded ${response.projects.length} projects`)
        
        // Set current project to the first project if none selected
        if (!currentProject.value && projects.value.length > 0) {
          currentProject.value = projects.value[0]
          console.log(`Set current project to: ${projects.value[0].title}`)
        }
        
        return true
      } else {
        error.value = response.message || 'Failed to load projects'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to load projects'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const createProject = async (projectData: {
    title: string
    description?: string
    type: string
    parent_id?: string
  }): Promise<Project | null> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await projectService.createProject(projectData)
      
      if (response.success && response.project) {
        projects.value.push(response.project)
        return response.project
      } else {
        error.value = response.message || 'Failed to create project'
        return null
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to create project'
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateProject = async (projectId: string, updates: Partial<Project>): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await projectService.updateProject(projectId, updates)
      
      if (response.success && response.project) {
        const index = projects.value.findIndex(p => p.id === projectId)
        if (index !== -1) {
          projects.value[index] = response.project
        }
        
        // Update current project if it was the one updated
        if (currentProject.value?.id === projectId) {
          currentProject.value = response.project
        }
        
        return true
      } else {
        error.value = response.message || 'Failed to update project'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to update project'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const deleteProject = async (projectId: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await projectService.deleteProject(projectId)
      
      if (response.success) {
        projects.value = projects.value.filter(p => p.id !== projectId)
        
        // Clear current project if it was deleted
        if (currentProject.value?.id === projectId) {
          currentProject.value = projects.value.length > 0 ? projects.value[0] : null
        }
        
        return true
      } else {
        error.value = response.message || 'Failed to delete project'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to delete project'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const setCurrentProject = (project: Project | null): void => {
    currentProject.value = project
  }

  const getProjectById = (projectId: string): Project | undefined => {
    return projects.value.find(p => p.id === projectId)
  }

  const getProjectChildren = (projectId: string): Project[] => {
    return projects.value.filter(p => (p.hierarchy?.parent_id || p.parent_id) === projectId)
  }

  const clearError = (): void => {
    error.value = null
  }

  const refreshProjects = async (): Promise<boolean> => {
    return loadUserProjects(true) // Force refresh
  }

  return {
    // State
    projects, currentProject, isLoading, error,
    // Getters
    getProjectsByType, getProjectsByStatus, getProjectsByDevelopmentLevel,
    currentProjectChildren, projectsByParent, currentProjectPath,
    // Actions
    loadUserProjects, createProject, updateProject, deleteProject,
    setCurrentProject, getProjectById, getProjectChildren,
    clearError, refreshProjects
  }
})